import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { useAdminAuth } from './hooks/useAdminAuth'
import { LoadingSpinner } from './components/common/UI'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import Landing from './pages/Landing'
import Auth from './pages/Auth'
import Merchant from './pages/merchant'
import Admin from './pages/admin'
import AdminLogin from './pages/admin/Login'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import Contact from './pages/Contact'
import Share from './pages/Share'

const ProtectedMerchantRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, isAuthenticated } = useAuth()
  
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
  
  if (!isAuthenticated || !user || user.isAdmin) {
    return <Navigate to="/merchant/login" replace />
  }
  
  return <>{children}</>
}

const ProtectedAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { admin, loading, isAuthenticated } = useAdminAuth()
  
  if (loading) return null
  
  if (!isAuthenticated || !admin || admin.status !== 'ACTIVE') {
    return <Navigate to="/admin" replace />
  }
  
  return <>{children}</>
}

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading, user, needsProfileCompletion } = useAuth()
  
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
  
  if (isAuthenticated && user && !needsProfileCompletion) {
    return <Navigate to="/merchant/dashboard" replace />
  }
  
  return <>{children}</>
}

const PublicAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAdminAuth()
  
  if (loading) return null
  
  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />
  }
  
  return <>{children}</>
}

export default function App() {
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-white">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/share" element={<Share />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          
          <Route path="/merchant/login" element={<PublicRoute><Auth /></PublicRoute>} />
          <Route path="/merchant/register" element={<PublicRoute><Auth /></PublicRoute>} />
          <Route path="/merchant/*" element={<ProtectedMerchantRoute><Merchant /></ProtectedMerchantRoute>} />
          
          <Route path="/admin" element={<PublicAdminRoute><AdminLogin /></PublicAdminRoute>} />
          <Route path="/admin/*" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </ErrorBoundary>
  )
}